import { useCallback, useRef, useState } from "react";
import { Image } from "../../elements/image/image";

type Position = {
  x: number;
  y: number;
};

type ZoomedImageProps = {
  imgSrc: string;
  imgAlt: string;
  onClick: () => void;
  className?: string;
};

const IMAGE_SIZE = 2000;
const PAN_RANGE_FACTOR = 2;
const WHEEL_SCROLL_SPEED = 0.5;
const VIEWPORT_CENTER = 0.5;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const getBounds = () => ({
  maxX: Math.max(0, (IMAGE_SIZE - window.innerWidth) / 2),
  maxY: Math.max(0, (IMAGE_SIZE - window.innerHeight) / 2),
});

export function ZoomedImage({
  imgSrc,
  imgAlt,
  onClick,
  className,
}: ZoomedImageProps) {
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });

  const touchDragStart = useRef<Position>({ x: 0, y: 0 });

  // Mouse position maps directly to pan offset:
  // mouse at top-left → reveal top-left of image, center → centered, etc.
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { maxX, maxY } = getBounds();
      setPosition({
        x:
          (VIEWPORT_CENTER - e.clientX / window.innerWidth) *
          PAN_RANGE_FACTOR *
          maxX,
        y:
          (VIEWPORT_CENTER - e.clientY / window.innerHeight) *
          PAN_RANGE_FACTOR *
          maxY,
      });
    },
    []
  );

  const handleWheel = useCallback((e: React.WheelEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { maxY } = getBounds();
    setPosition((prev) => ({
      ...prev,
      y: clamp(prev.y - e.deltaY * WHEEL_SCROLL_SPEED, -maxY, maxY),
    }));
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      if (e.touches.length !== 1) {
        return;
      }
      touchDragStart.current = {
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      };
    },
    [position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLButtonElement>) => {
      if (e.touches.length !== 1) {
        return;
      }
      const { maxX, maxY } = getBounds();
      setPosition({
        x: clamp(e.touches[0].clientX - touchDragStart.current.x, -maxX, maxX),
        y: clamp(e.touches[0].clientY - touchDragStart.current.y, -maxY, maxY),
      });
    },
    []
  );

  return (
    <button
      className={`fixed inset-0 overflow-hidden border-0 bg-transparent p-0${className ? ` ${className}` : ""}`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      onWheel={handleWheel}
      style={{ touchAction: "none" }}
      type="button"
    >
      <Image
        alt={imgAlt}
        aspectRatio="1:1"
        className="absolute top-1/2 left-1/2 h-[125rem] w-[125rem] max-w-none bg-white"
        src={imgSrc}
        style={{
          transform: `translate(
            calc(-50% + ${position.x}px),
            calc(-50% + ${position.y}px)
          )`,
        }}
        unStyled
      />
    </button>
  );
}
